import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import BookCard from './BookCard';
import BookListItem from './BookListItem';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import Button from '../common/Button';
import { useState } from 'react';
import { paginate } from '../../utils/helpers';
import { getFadeUpVariants, getStaggerContainer } from '../../utils/motionVariants';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const BookGrid = ({ books, loading, isAdmin = false, itemsPerPage = 20, viewMode = 'grid', onClearFilters }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  // Skeleton Loading State
  if (loading) {
    return (
      <div className={`grid gap-6 ${viewMode === 'list' ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4'}`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-dark-surface p-4 ${viewMode === 'list' ? 'h-48' : 'h-96'}`}>
            <div className={viewMode === 'list' ? "flex gap-4 h-full" : "h-full"}>
              <Skeleton className={viewMode === 'list' ? "w-32 h-full rounded-lg" : "w-full aspect-[2/3] mb-4 rounded-lg"} />
              <div className="flex-1 space-y-3 pt-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!books || books.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-24 bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm"
      >
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary-50 dark:bg-primary-900/20 mb-6">
          <BookOpen className="h-12 w-12 text-primary-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {t('lists.no_books')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6 px-4">
          {t('lists.no_books_desc')}
        </p>
        {onClearFilters && (
          <Button variant="outline" onClick={onClearFilters}>
            {t('filters.clear')}
          </Button>
        )}
      </motion.div>
    );
  }

  const paginatedData = paginate(books, currentPage, itemsPerPage);
  const fadeUp = getFadeUpVariants(reduceMotion);
  const stagger = getStaggerContainer(reduceMotion);

  return (
    <div>
      <motion.div
        className={
          viewMode === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-8'
            : 'space-y-4 mb-8'
        }
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="popLayout">
          {paginatedData.data.map((book) => (
            viewMode === 'grid' ? (
              <motion.div 
                key={book.id} 
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <BookCard book={book} isAdmin={isAdmin} />
              </motion.div>
            ) : (
              <motion.div 
                key={book.id} 
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <BookListItem book={book} isAdmin={isAdmin} />
              </motion.div>
            )
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Pagination */}
      {paginatedData.totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={paginatedData.totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Results Info */}
      <div className="text-center mt-6 text-sm text-gray-500 dark:text-gray-400">
        {t('lists.showing_results', {
          start: (currentPage - 1) * itemsPerPage + 1,
          end: Math.min(currentPage * itemsPerPage, books.length),
          total: books.length
        })} {t('lists.items_books')}
      </div>
    </div>
  );
};

export default BookGrid;
